import { BUSINESS_TIMEZONE, HttpErrors } from '@omecare/utils';
import { intlFormat } from 'date-fns/intlFormat';
import { I18nTranslate } from '../../i18n';

const ACCOUNTING_VALIDATE_REPORT_TYPE =
  'accounting_agenda_slot_validate_report_error';
const ACCOUNTING_DOCTOLIB_DATA_MISSING_TYPE =
  'accounting_agenda_slots_doctolib_data_missing';
const ACCOUNTING_MORNING_DOCTOLIB_NO_INTERSECTION_TYPE =
  'accounting_agenda_slots_morning_doctolib_data_no_intersection';

/**
 * Error types whose translation takes a `{{slots}}` list of offending slots.
 */
const SLOT_LISTING_ERROR_TYPES: readonly string[] = [
  ACCOUNTING_VALIDATE_REPORT_TYPE,
  ACCOUNTING_DOCTOLIB_DATA_MISSING_TYPE,
  ACCOUNTING_MORNING_DOCTOLIB_NO_INTERSECTION_TYPE,
];

/**
 * Minimal shape of a slot pointer carried by the accounting errors, in either
 * the mapped BusinessError body (`data.context.*`) or the legacy non-dazz body
 * (`data.data.*`).
 */
export type AccountingErrorSlot = {
  id: string;
  startAt: Date | string;
  periodOfTheDay: string;
};

/**
 * Union of the slot buckets carried by the accounting error payloads:
 * `agendaSlots` for the doctolib-data errors, the two `slotsWith*` buckets for
 * the validate-report error.
 */
type AccountingErrorPayload = {
  agendaSlots?: AccountingErrorSlot[];
  slotsWithMissingInformation?: AccountingErrorSlot[];
  slotsWithInvalidInformation?: AccountingErrorSlot[];
};

/**
 * Formats a single offending slot as "<period> <dd/mm/yyyy>" for the toast list.
 *
 * @param params.slot - the slot pointer to format
 * @param params.t - i18n translate function (for the period label)
 * @returns a human-readable slot label
 */
export const formatAccountingErrorSlot = ({
  slot,
  t,
}: {
  slot: AccountingErrorSlot;
  t: I18nTranslate;
}): string => {
  const period = t(`period-of-day.${slot.periodOfTheDay}`);
  const date = intlFormat(
    new Date(slot.startAt),
    {
      timeZone: BUSINESS_TIMEZONE,
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
    },
    { locale: 'fr' },
  );
  return `${period} ${date}`;
};

/**
 * Collects the offending slots from an accounting error payload, de-duplicating
 * by id (a slot may appear in more than one bucket).
 *
 * @param payload - the error payload, or undefined when absent
 * @returns the de-duplicated slot pointers
 */
const collectPayloadSlots = (
  payload: AccountingErrorPayload | undefined,
): AccountingErrorSlot[] => {
  if (!payload) {
    return [];
  }
  const seen = new Set<string>();
  const slots: AccountingErrorSlot[] = [];
  const buckets = [
    ...(payload.agendaSlots ?? []),
    ...(payload.slotsWithMissingInformation ?? []),
    ...(payload.slotsWithInvalidInformation ?? []),
  ];
  for (const slot of buckets) {
    if (seen.has(slot.id)) {
      continue;
    }
    seen.add(slot.id);
    slots.push(slot);
  }
  return slots;
};

/**
 * Extracts the offending slots carried by an accounting error response.
 *
 * Business errors mapped by the API `ExceptionMapper` carry them under
 * `data.context`; the legacy non-dazz import controller carries them under
 * `data.data`.
 *
 * @param error - the HTTP error thrown by the http service
 * @returns the de-duplicated slot pointers, empty when the payload carries none
 */
export const getAccountingErrorSlots = (
  error: HttpErrors,
): AccountingErrorSlot[] => {
  const body = error.data as
    | { context?: AccountingErrorPayload; data?: AccountingErrorPayload }
    | undefined;
  return collectPayloadSlots(body?.context ?? body?.data);
};

/**
 * Builds the i18next interpolation params for a business-error toast.
 *
 * Most error types need none. The accounting errors that point at specific
 * slots are enriched with a `slots` string listing them (period + date) so the
 * toast names exactly what to fix, instead of a vague "one of the slots"
 * sentence. The value is prefixed with " : " when non-empty so the base sentence
 * stays grammatical when no slots could be extracted.
 *
 * @param params.error - the HTTP error thrown by the http service
 * @param params.errorType - the resolved snake_case business-error type
 * @param params.t - i18n translate function
 * @returns interpolation params for `t(key, params)` (empty object by default)
 */
export const getErrorToastParams = ({
  error,
  errorType,
  t,
}: {
  error: HttpErrors;
  errorType: string;
  t: I18nTranslate;
}): Record<string, unknown> => {
  if (!SLOT_LISTING_ERROR_TYPES.includes(errorType)) {
    return {};
  }
  const slots = getAccountingErrorSlots(error);
  if (slots.length === 0) {
    return { slots: '' };
  }
  const labels: string[] = [];
  for (const slot of slots) {
    labels.push(formatAccountingErrorSlot({ slot, t }));
  }
  return { slots: ` : ${labels.join(', ')}` };
};
